import { z } from 'zod'
import { leaveTypeIdSchema } from './leave.js'

export const balanceSchema = z.object({
  employeeId: z.guid(),
  leaveType: leaveTypeIdSchema,
  year: z.number().int().min(2000).max(2100),
  /**
   * Pro-rated yearly slice (e.g. a March joiner gets ~13.5 of 16). The
   * AccrualBar's denominator. For non-annual leave types and post-first-
   * year employees this equals `accruedEntitlement`.
   */
  entitlement: z.number().nonnegative(),
  /**
   * Running cap on what the employee can apply for *right now*. For an
   * annual-leave first-year employee this is the monthly drip (1/12 per
   * month from join month forward). For everything else, equal to
   * `entitlement`. `available` is computed against this, not the yearly
   * slice — so an employee can never spend leave they haven't earned.
   *
   * Optional in the schema for backward compatibility with mock
   * fixtures and stories; consumers should fall back to `entitlement`
   * when absent. The real wire always carries it.
   */
  accruedEntitlement: z.number().nonnegative().optional(),
  used: z.number().nonnegative(),
  pending: z.number().nonnegative(),
  carryOver: z.number().nonnegative(),
  available: z.number(),
  /**
   * Structural eligibility as of today, independent of the balance number.
   * `false` means the employee cannot apply for this type yet even though they
   * hold an entitlement (e.g. paternity/marriage/birthday under 12 months'
   * service, or during probation) — the tile renders "NOT YET ELIGIBLE" and the
   * apply picker hides the type. Optional so older payloads/fixtures/stories that
   * omit it read as eligible — only an explicit `false` marks a type ineligible
   * (consumers gate on `eligible === false`). The real wire always carries it.
   */
  eligible: z.boolean().optional(),
  /** User-facing reason the type is not yet eligible; null/absent when eligible. */
  ineligibleReason: z.string().nullish(),
})

export type Balance = z.infer<typeof balanceSchema>
